import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useEffect } from "react";
import { formatEuro, formatEuroCompact } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useQuery } from "@tanstack/react-query";

interface MonthlyData {
  month: string;
  revenue: number;
  expenses: number;
  cumulativeRevenue: number;
  cumulativeExpenses: number;
}

const monthNames = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg border border-border bg-popover p-3 shadow-lg">
        <p className="font-medium text-foreground mb-2">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {formatEuro(entry.value, false)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export function RevenueChart() {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear.toString());
  const [data, setData] = useState<MonthlyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCumulativeRevenue, setShowCumulativeRevenue] = useState(false);
  const [showCumulativeExpenses, setShowCumulativeExpenses] = useState(false);
  const [availableYears, setAvailableYears] = useState<string[]>([currentYear.toString()]);
  const { workspace } = useWorkspace();
  const workspaceId = workspace?.id || null;
  const { data: revenueResult, isLoading: revenueLoading } = useQuery({
    queryKey: ["monthly-revenue", workspaceId, selectedYear],
    enabled: !!workspaceId,
    queryFn: async () => {
      if (!workspaceId) return [];
      const { data, error } = await supabase.rpc("get_monthly_revenue_expenses", {
        p_year: Number(selectedYear), p_workspace_id: workspaceId,
      });
      if (error) throw error;
      return data;
    },
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (workspaceId) fetchData();
  }, [selectedYear, workspaceId, revenueResult, revenueLoading]);

  const fetchData = async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const year = parseInt(selectedYear);

      const rpcData = revenueResult;
      {
        let accRevenue = 0;
        let accExpenses = 0;
        const monthlyData: MonthlyData[] = monthNames.map((month, idx) => {
          const row = rpcData?.find((r: any) => r.month_index === idx + 1);
          const revenue = Number(row?.total_revenue) || 0;
          const expenses = Number(row?.total_expenses) || 0;
          accRevenue += revenue;
          accExpenses += expenses;
          return {
            month,
            revenue,
            expenses,
            cumulativeRevenue: accRevenue,
            cumulativeExpenses: accExpenses,
          };
        });
        setData(monthlyData);
      }

      const years = new Set<string>();
      years.add(currentYear.toString());
      years.add((currentYear - 1).toString());
      setAvailableYears(Array.from(years).sort().reverse());
    } catch (error) {
      console.error("Error fetching revenue data:", error);
    } finally {
      setLoading(revenueLoading);
    }
  };

  if (loading) {
    return (
      <div className="chart-container animate-slide-in-up" style={{ animationDelay: "200ms" }}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-display text-lg font-semibold text-foreground">
              Ingresos vs Gastos
            </h3>
            <p className="text-sm text-muted-foreground">
              Análisis financiero mensual
            </p>
          </div>
        </div>
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  const hasData = data.some(d => d.revenue > 0 || d.expenses > 0);

  return (
    <div className="chart-container animate-slide-in-up" style={{ animationDelay: "200ms" }}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground">
            Ingresos vs Gastos
          </h3>
          <p className="text-sm text-muted-foreground">
            Análisis financiero mensual
          </p>
        </div>
        <Select value={selectedYear} onValueChange={setSelectedYear}>
          <SelectTrigger className="w-28 bg-secondary border-border">
            <SelectValue placeholder="Año" />
          </SelectTrigger>
          <SelectContent className="bg-popover border-border">
            {availableYears.map((year) => (
              <SelectItem key={year} value={year}>{year}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {!hasData ? (
        <div className="h-80 flex items-center justify-center text-muted-foreground">
          No hay datos financieros para {selectedYear}
        </div>
      ) : (
        <div className="overflow-x-auto -mx-4">
          <div className="min-w-[350px] px-4 h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(213, 94%, 55%)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(213, 94%, 55%)" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="expensesGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(187, 85%, 53%)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(187, 85%, 53%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(222, 47%, 22%)" />
                <XAxis
                  dataKey="month"
                  stroke="hsl(215, 20%, 65%)"
                  tick={{ fill: "hsl(215, 20%, 65%)", fontSize: 10 }}
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={45}
                />
                <YAxis
                  stroke="hsl(215, 20%, 65%)"
                  tick={{ fill: "hsl(215, 20%, 65%)", fontSize: 10 }}
                  tickFormatter={(value) => formatEuroCompact(value)}
                  width={45}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  name="Ingresos"
                  stroke="hsl(213, 94%, 55%)"
                  strokeWidth={2}
                  fill="url(#revenueGradient)"
                />
                <Area
                  type="monotone"
                  dataKey="expenses"
                  name="Gastos"
                  stroke="hsl(187, 85%, 53%)"
                  strokeWidth={2}
                  fill="url(#expensesGradient)"
                />
                {showCumulativeRevenue && <Line
                  type="monotone"
                  dataKey="cumulativeRevenue"
                  name="Ingresos acumulados"
                  stroke="hsl(var(--success))"
                  strokeWidth={2.5}
                  dot={false}
                />}
                {showCumulativeExpenses && <Line
                  type="monotone"
                  dataKey="cumulativeExpenses"
                  name="Gastos acumulados"
                  stroke="hsl(var(--destructive))"
                  strokeWidth={2.5}
                  dot={false}
                />}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-4">
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-full bg-primary" />
          <span className="text-sm text-muted-foreground">Ingresos</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-full bg-accent" />
          <span className="text-sm text-muted-foreground">Gastos</span>
        </div>
        <div className="flex items-center gap-2">
          <Switch id="cumulative-revenue" checked={showCumulativeRevenue} onCheckedChange={setShowCumulativeRevenue} />
          <div className="h-0.5 w-6 bg-success" />
          <Label htmlFor="cumulative-revenue" className="text-sm text-muted-foreground">Ingresos acumulados</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch id="cumulative-expenses" checked={showCumulativeExpenses} onCheckedChange={setShowCumulativeExpenses} />
          <div className="h-0.5 w-6 bg-destructive" />
          <Label htmlFor="cumulative-expenses" className="text-sm text-muted-foreground">Gastos acumulados</Label>
        </div>
      </div>
    </div>
  );
}
